import React, { useRef, useState } from "react";
import { adminApi } from "../../apiAdmin";
import { withQuietRetry } from "../../utils/adminRetry";
import AdminLoadFailed from "../AdminLoadFailed";
import { formatDuration } from "../../utils/formatDuration";
import { adminStatusClass, adminStatusLabel } from "../../utils/orderMoney";
import { formatBatchAvgValue, formatMoneyByCurrency, moneyByCurrencyFrom } from "../../utils/currency";
import { Order, PayoutEvent } from "./types";

const renderAmountCell = (o: Order) => {
  const cur = (o.currency || "usd").toUpperCase();
  // Counter pending: DJ declined with counter, fan hasn't responded yet
  if (o.counterAmount && o.declineReason === "more_money") {
    const diff = o.counterAmount - o.amount;
    return (
      <>
        {cur} {o.counterAmount.toFixed(2)}
        <span className="amount-addon amount-counter" title={`Countered — Original: ${cur} ${o.amount.toFixed(2)}`}>+{diff.toFixed(2)}</span>
      </>
    );
  }
  // Counter accepted or boost: originalAmount preserved
  if (o.originalAmount != null && o.originalAmount !== o.amount) {
    const diff = o.amount - o.originalAmount;
    const isBoosted = !!o.isPriority;
    return (
      <>
        {cur} {o.amount.toFixed(2)}
        <span
          className={`amount-addon ${isBoosted ? "amount-boost" : "amount-counter"}`}
          title={`${isBoosted ? "Boosted" : "Countered"} — Original: ${cur} ${o.originalAmount.toFixed(2)}`}
        >
          +{diff.toFixed(2)}
        </span>
      </>
    );
  }
  // Fast pass: bid was 2x DJ minimum, show the split
  if (o.tier === "fast") {
    const half = o.amount / 2;
    return (
      <>
        {cur} {o.amount.toFixed(2)}
        <span className="amount-addon amount-fast" title={`FastPass — base ${cur} ${half.toFixed(2)} + ${cur} ${half.toFixed(2)} premium`}>+{half.toFixed(2)}</span>
      </>
    );
  }
  return <>{cur} {o.amount.toFixed(2)}</>;
};

const renderStatusCell = (o: Order, size: "sm" | "xs" = "sm") => {
  if (o.status === "declined" && o.declineReason) {
    const reason = o.declineReason === "pas_ambience" ? "Not the Vibe"
      : o.declineReason === "no_access" ? "No Access to Song"
      : o.declineReason === "already_played" ? "Already Played"
      : o.declineReason === "more_money" ? "Counter Offered"
      : o.declineReason;
    return (
      <span className={`badge badge-${size} badge-declined adm-help`} title={reason}>
        declined *
      </span>
    );
  }
  return <span className={`badge badge-${size} badge-${adminStatusClass(o)}`}>{adminStatusLabel(o)}</span>;
};

// Payout History — one row per cashout batch; a row expands to the orders it
// paid (fetched on first open). Moved out of pages/AdminDjProfile.tsx in the
// Phase C split; the card renders nothing until the talent has a payout (or
// says "Couldn't load" when the history didn't load).
export function DjPayoutHistory({ payouts, failed, onRetry }: { payouts: PayoutEvent[]; failed: boolean; onRetry: () => void }) {
  const [expandedPayoutId, setExpandedPayoutId] = useState<string | null>(null);
  const [payoutOrders, setPayoutOrders] = useState<Order[]>([]);
  const [payoutOrdersLoading, setPayoutOrdersLoading] = useState(false);
  const [payoutOrdersFailed, setPayoutOrdersFailed] = useState(false);
  // The batch whose orders are wanted: an answer for a batch already closed
  // or swapped is dropped.
  const ordersFor = useRef<string | null>(null);

  const loadPayoutOrders = async (payoutId: string) => {
    ordersFor.current = payoutId;
    setPayoutOrdersLoading(true);
    setPayoutOrdersFailed(false);
    try {
      const res = await withQuietRetry(() => adminApi.get(`/payouts/${payoutId}/orders`));
      if (ordersFor.current === payoutId) setPayoutOrders(res.data);
    } catch {
      if (ordersFor.current === payoutId) {
        setPayoutOrders([]);
        setPayoutOrdersFailed(true);
      }
    } finally {
      if (ordersFor.current === payoutId) setPayoutOrdersLoading(false);
    }
  };

  const togglePayoutExpand = async (payoutId: string) => {
    if (expandedPayoutId === payoutId) {
      ordersFor.current = null;
      setExpandedPayoutId(null);
      setPayoutOrders([]);
      return;
    }
    setExpandedPayoutId(payoutId);
    await loadPayoutOrders(payoutId);
  };

  return (
    <>
      {/* A history that didn't load says so, instead of hiding the card. */}
      {failed && payouts.length === 0 && (
        <div className="adm-card adm-mb-16">
          <h2 className="adm-section-title adm-mb-12">Payout History</h2>
          <AdminLoadFailed what="payout history" onRetry={onRetry} />
        </div>
      )}
      {payouts.length > 0 && (
        <div className="adm-card adm-mb-16">
          <h2 className="adm-section-title adm-mb-12">Payout History</h2>
          <div className="adm-table-wrapper">
            <table className="adm-table-light adm-table-compact sticky-header">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Gross</th>
                  <th>Net (Talent)</th>
                  <th>Orders</th>
                  <th>Accepted</th>
                  <th>Declined</th>
                  <th>Avg. Value</th>
                  <th>Avg. Response</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => {
                  const isExpanded = expandedPayoutId === p._id;
                  return (
                    <React.Fragment key={p._id}>
                      <tr
                        className="hover-row clickable adm-pointer"
                        onClick={() => togglePayoutExpand(p._id)}
                      >
                        <td>
                          <span style={{ marginRight: 6, display: "inline-block", transition: "transform 0.2s", transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)", fontSize: "0.7rem" }}>
                            ▶
                          </span>
                          {new Date(p.createdAt).toLocaleDateString()}
                        </td>
                        <td className="muted-text">{new Date(p.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                        <td className="adm-semibold">{p.totalsByCurrency ? formatMoneyByCurrency(moneyByCurrencyFrom(p.totalsByCurrency, "gross")) : `$${p.totalGross.toFixed(2)}`}</td>
                        <td className="adm-bold adm-green">{p.totalsByCurrency ? formatMoneyByCurrency(moneyByCurrencyFrom(p.totalsByCurrency, "net")) : `$${p.totalNet.toFixed(2)}`}</td>
                        <td>{p.ordersCount}</td>
                        <td className="adm-green adm-semibold">{p.ordersAccepted}</td>
                        <td className="adm-red">{p.ordersDeclined}</td>
                        <td>{formatBatchAvgValue(p.totalsByCurrency, p.avgRequestValue)}</td>
                        <td className="muted-text adm-fs-78">
                          {p.avgResponseMs != null ? formatDuration(p.avgResponseMs) : "—"}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={9} className="adm-expand-cell">
                            <div className="payout-expand-panel">
                              {payoutOrdersLoading ? (
                                <p className="muted-text adm-fs-80" style={{ padding: "12px 16px", margin: 0 }}>Loading orders...</p>
                              ) : payoutOrdersFailed ? (
                                <div style={{ padding: "4px 16px" }}>
                                  <AdminLoadFailed inline what="this cashout's orders" onRetry={() => loadPayoutOrders(p._id)} />
                                </div>
                              ) : payoutOrders.length === 0 ? (
                                <p className="muted-text adm-fs-80" style={{ padding: "12px 16px", margin: 0 }}>No orders found for this cashout.</p>
                              ) : (
                                <table className="adm-table-light adm-table-xs adm-w-full" style={{ background: "#f9fafb" }}>
                                  <thead>
                                    <tr>
                                      <th style={{ width: "38%" }}>Track</th>
                                      <th style={{ width: "14%" }}>Amount</th>
                                      <th style={{ width: "10%" }}>Status</th>
                                      <th style={{ width: "18%" }}>Client</th>
                                      <th style={{ width: "20%" }}>Date</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {payoutOrders.map((o) => (
                                      <tr key={o._id}>
                                        <td className="adm-clip-cell">
                                          <span className="truncate-inline" title={o.searchQuery}>
                                            {o.searchQuery}
                                          </span>
                                        </td>
                                        <td className="adm-bold adm-nowrap">
                                          {renderAmountCell(o)}
                                        </td>
                                        <td>
                                          {renderStatusCell(o, "xs")}
                                        </td>
                                        <td className="adm-clip-cell">
                                          <span
                                            className="truncate-inline adm-fs-72"
                                            title={[o.customerName, o.clientInstagram ? `@${o.clientInstagram}` : null, o.customerEmail, o.clientNickname, o.cardLast4 ? `•••• ${o.cardLast4}` : null].filter(Boolean).join(" | ") || undefined}
                                          >
                                            {o.customerName || (o.clientInstagram ? `@${o.clientInstagram}` : null) || o.customerEmail || o.clientNickname || (o.cardLast4 ? `•••• ${o.cardLast4}` : null) || "—"}
                                          </span>
                                        </td>
                                        <td className="muted-text adm-fs-72 adm-nowrap">
                                          {new Date(o.createdAt).toLocaleDateString()}{" "}
                                          {new Date(o.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
